import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export default function TradePingDialog({ cardId, cardName, ownerName }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [needsContact, setNeedsContact] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post("/trades", { card_id: cardId, message });
      toast.success(`Trade ping sent to ${ownerName}`);
      setOpen(false);
      setMessage("");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.includes("contact info")) {
        setNeedsContact(true);
      } else if (e.response?.status === 409) {
        toast.info("You already have a pending ping on this card");
        setOpen(false);
      } else if (e.response?.status === 401) {
        toast.error("Log in to ping trades");
      } else {
        toast.error(detail || "Couldn't send the trade ping");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`trade-ping-button-${cardId}`}
        title="Ping a trade for this card"
        onClick={() => setOpen(true)}
        className="w-7 h-7 rounded-full flex items-center justify-center bg-zinc-900 border border-[#A78BFA]/60 text-[#C4B5FD] shadow-lg hover:bg-[#7C3AED] hover:text-white transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="trade-ping-dialog" className="bg-[#111113] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Ping a trade</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Ask {ownerName} about their <span className="text-white">{cardName}</span>. If they accept,
              you'll see each other's contact info to arrange it yourselves.
            </DialogDescription>
          </DialogHeader>
          {needsContact ? (
            <div data-testid="trade-ping-needs-contact" className="text-sm text-zinc-300 space-y-3">
              <p>You need contact info (e.g. your Discord handle) on your profile first, so the other collector can reach you if they accept.</p>
              <Link to="/settings">
                <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full">Add contact info in Settings</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <Textarea
                data-testid="trade-ping-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional message — e.g. what you'd offer for it"
                maxLength={280}
                className="bg-black/30 border-white/10 text-sm min-h-[80px]"
              />
              <p className="text-[10px] text-zinc-600">
                CardArk only connects collectors. Any trade is arranged entirely between you and the other
                user, at your own risk — CardArk is not a party to, and not responsible for, any trade.
              </p>
              <Button
                data-testid="trade-ping-send-button"
                onClick={send}
                disabled={sending}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full w-full"
              >
                {sending ? "Sending..." : "Send trade ping"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
